import { deriveIdentityKey, mnemonicToSeed, validateMnemonic } from "@link/crypto";
import { decryptIdentityBackup, decryptRecoveryPhrase, encryptRecoveryBackup, passkeyPrfInput } from "@link/crypto/passkey-backup";
import { enrollmentProofBytes, fromBase64url, removalProofBytes, toBase64url, type BackupBinding, type KeyBackup, type SignupPasskey } from "@link/shared/api/passkeys";
import { fetchJson } from "./http";
import { loadIdentity } from "./identity";

type DescriptorJSON = { id: string; type: "public-key"; transports?: AuthenticatorTransport[] };
interface CreationJSON extends Omit<PublicKeyCredentialCreationOptions, "challenge" | "user" | "excludeCredentials" | "extensions"> {
  challenge: string;
  user: { id: string; name: string; displayName: string };
  excludeCredentials?: DescriptorJSON[];
}
interface RequestJSON extends Omit<PublicKeyCredentialRequestOptions, "challenge" | "allowCredentials" | "extensions"> {
  challenge: string;
  allowCredentials?: DescriptorJSON[];
}
export type PasskeyTarget = "device" | "phone";
interface EnrollmentFlow { flowId: string; options: CreationJSON; unlockOptions: RequestJSON; accountXid: string; identityPublicKey: string }
type PrfOutputs = { prf?: { enabled?: boolean; results?: { first: ArrayBuffer } } };
export const PRF_UNAVAILABLE = "This device or password manager does not support Link’s encrypted backup. Try your phone or another password manager, or use a recovery phrase. No unencrypted backup was saved.";

export function passkeysAvailable() { return typeof window !== "undefined" && window.isSecureContext && typeof PublicKeyCredential !== "undefined" && !!navigator.credentials; }

function requireSupport() {
  if (!passkeysAvailable()) throw new Error("Passkeys need a supported browser on HTTPS (or localhost). Your recovery phrase still works.");
}

function prfExtensions(first: Uint8Array): AuthenticationExtensionsClientInputs {
  return { prf: { eval: { first } } } as AuthenticationExtensionsClientInputs;
}

export function requirePasskeyPrfResult(credential: PublicKeyCredential): Uint8Array {
  const first = (credential.getClientExtensionResults() as PrfOutputs).prf?.results?.first;
  if (!first || first.byteLength !== 32) throw new Error(PRF_UNAVAILABLE);
  return new Uint8Array(first);
}

// Whitelist fields: NEVER send getClientExtensionResults()/toJSON() wholesale.
export function publicAssertion(credential: PublicKeyCredential) {
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id, rawId: toBase64url(new Uint8Array(credential.rawId)), type: "public-key" as const,
    response: { clientDataJSON: toBase64url(new Uint8Array(response.clientDataJSON)), authenticatorData: toBase64url(new Uint8Array(response.authenticatorData)), signature: toBase64url(new Uint8Array(response.signature)), ...(response.userHandle ? { userHandle: toBase64url(new Uint8Array(response.userHandle)) } : {}) },
    clientExtensionResults: {},
  };
}

function publicRegistration(credential: PublicKeyCredential) {
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id, rawId: toBase64url(new Uint8Array(credential.rawId)), type: "public-key" as const,
    response: { clientDataJSON: toBase64url(new Uint8Array(response.clientDataJSON)), attestationObject: toBase64url(new Uint8Array(response.attestationObject)) },
    clientExtensionResults: {},
  };
}

async function getCredential(options: RequestJSON, first: Uint8Array, target?: PasskeyTarget): Promise<PublicKeyCredential> {
  const credential = await navigator.credentials.get({ publicKey: {
    ...options, ...(target ? { hints: [target === "phone" ? "hybrid" : "client-device"] } : {}), challenge: fromBase64url(options.challenge),
    allowCredentials: options.allowCredentials?.map(item => ({ ...item, id: fromBase64url(item.id) })),
    userVerification: "required", extensions: prfExtensions(first),
  } }) as PublicKeyCredential | null;
  if (!credential) throw new Error("No passkey selected.");
  return credential;
}

async function signProof(key: CryptoKey, bytes: Uint8Array): Promise<string> {
  return toBase64url(new Uint8Array(await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, key, bytes)));
}

// Generate and verify the encrypted backup entirely on the client. Only the
// public WebAuthn proof, ciphertext and identity signature leave this function.
async function prepareBackup(phrase: string, flow: EnrollmentFlow, target: PasskeyTarget): Promise<SignupPasskey> {
  const seed = await mnemonicToSeed(phrase);
  let secret: Uint8Array | undefined;
  let credentialCreated = false;
  try {
    const identity = await deriveIdentityKey(seed);
    if (flow.identityPublicKey !== toBase64url(identity.publicKeyRaw)) throw new Error("Account identity changed. Try again.");
    const rpId = flow.options.rp.id!;
    const first = await passkeyPrfInput(rpId);
    const created = await navigator.credentials.create({ publicKey: {
      ...flow.options,
      hints: [target === "phone" ? "hybrid" : "client-device"],
      challenge: fromBase64url(flow.options.challenge), user: { ...flow.options.user, id: fromBase64url(flow.options.user.id) },
      excludeCredentials: flow.options.excludeCredentials?.map(item => ({ ...item, id: fromBase64url(item.id) })),
      extensions: prfExtensions(first),
    } as PublicKeyCredentialCreationOptions }) as PublicKeyCredential | null;
    if (!created) throw new DOMException("Setup cancelled", "NotAllowedError");
    credentialCreated = true;
    const creationPrf = (created.getClientExtensionResults() as PrfOutputs).prf;
    if (creationPrf?.results?.first) new Uint8Array(creationPrf.results.first).fill(0);
    if (!creationPrf?.enabled) throw new Error(`${PRF_UNAVAILABLE} An unused Link entry may remain in your password manager.`);
    // This second confirmation checks that the saved method actually works.
    const assertion = await getCredential({ ...flow.unlockOptions, allowCredentials: [{ type: "public-key", id: created.id }] }, first, target);
    if (assertion.id !== created.id) throw new Error("A different passkey was selected.");
    secret = requirePasskeyPrfResult(assertion);
    const binding = { rpId, accountXid: flow.accountXid, identityPublicKey: flow.identityPublicKey, credentialId: created.id };
    const backup = await encryptRecoveryBackup(phrase, secret, binding);
    const check = await decryptIdentityBackup(backup, secret, binding);
    try { if (check.length !== seed.length || check.some((b, i) => b !== seed[i])) throw new Error("Encrypted backup verification failed"); }
    finally { check.fill(0); }
    const proof = await signProof(identity.privateKey, enrollmentProofBytes(flow.flowId, binding, backup));
    return { flowId: flow.flowId, registration: publicRegistration(created), assertion: publicAssertion(assertion), backup, proof };
  } catch (error) {
    if (credentialCreated && error instanceof Error && error.name === "NotAllowedError") throw new Error("Setup wasn’t finished. You can try again. An unused Link entry may remain in your password manager; it hasn’t been added to your Link account.");
    throw error;
  } finally { seed.fill(0); secret?.fill(0); }
}

export async function prepareSignupPasskey(phrase: string, username: string, displayName: string | undefined, target: PasskeyTarget): Promise<SignupPasskey> {
  requireSupport();
  const seed = await mnemonicToSeed(phrase);
  try {
    const identity = await deriveIdentityKey(seed);
    const publicKey = btoa(String.fromCharCode(...identity.publicKeyRaw));
    const flow = await fetchJson<EnrollmentFlow>("/v1/passkeys/signup/options", { method: "POST", token: null, body: { username, display_name: displayName, public_key_b64: publicKey } });
    return await prepareBackup(phrase, flow, target);
  } finally { seed.fill(0); }
}

export async function enrollPasskey(phrase: string, accountXid: string, label: string): Promise<void> {
  requireSupport();
  if (!(await validateMnemonic(phrase)).ok) throw new Error("Check your 12-word recovery phrase. It stays on this device.");
  const seed = await mnemonicToSeed(phrase);
  try {
    const identity = await deriveIdentityKey(seed);
    const local = await loadIdentity(accountXid);
    if (!local || toBase64url(local.publicKeyRaw) !== toBase64url(identity.publicKeyRaw)) throw new Error("This phrase belongs to a different account.");
    const flow = await fetchJson<EnrollmentFlow>("/v1/passkeys/enroll/options", { method: "POST" });
    if (flow.accountXid !== accountXid) throw new Error("Account identity changed. Sign in again.");
    const setup = await prepareBackup(phrase, flow, "device");
    await fetchJson("/v1/passkeys/enroll", { method: "POST", body: { ...setup, label: label.trim() || "My passkey" } });
  } finally { seed.fill(0); }
}

// Caller must wipe the returned seed in finally, after existing identity login.
export async function unlockPasskeySeed(target?: PasskeyTarget, revealRecovery = false): Promise<{ seed: Uint8Array; accountXid: string; recoveryPhrase?: string }> {
  requireSupport();
  const flow = await fetchJson<{ flowId: string; options: RequestJSON }>("/v1/passkeys/unlock/options", { method: "POST", token: null });
  const rpId = flow.options.rpId!;
  const assertion = await getCredential(flow.options, await passkeyPrfInput(rpId), target);
  const secret = requirePasskeyPrfResult(assertion);
  let seed: Uint8Array | undefined;
  try {
    const result = await fetchJson<{ backup: KeyBackup; binding: BackupBinding }>("/v1/passkeys/unlock", { method: "POST", token: null, body: { flowId: flow.flowId, assertion: publicAssertion(assertion) } });
    if (result.binding.rpId !== rpId || result.binding.credentialId !== assertion.id || new TextDecoder().decode((assertion.response as AuthenticatorAssertionResponse).userHandle!) !== result.binding.accountXid) throw new Error("Backup identity mismatch");
    seed = await decryptIdentityBackup(result.backup, secret, result.binding);
    const identity = await deriveIdentityKey(seed);
    if (toBase64url(identity.publicKeyRaw) !== result.binding.identityPublicKey) throw new Error("Backup identity mismatch");
    return { seed, accountXid: result.binding.accountXid, ...(revealRecovery && result.backup.version === 2 ? { recoveryPhrase: await decryptRecoveryPhrase(result.backup, secret, result.binding) } : {}) };
  } catch (error) { seed?.fill(0); throw error; }
  finally { secret.fill(0); }
}

export async function removePasskey(accountXid: string, credentialId: string) {
  const identity = await loadIdentity(accountXid);
  if (!identity) throw new Error("Sign in again to manage passkeys.");
  const { flowId } = await fetchJson<{ flowId: string }>("/v1/passkeys/remove/options", { method: "POST", body: { credentialId } });
  await fetchJson("/v1/passkeys/remove", { method: "POST", body: { flowId, proof: await signProof(identity.privateKey, removalProofBytes(flowId, accountXid, credentialId)) } });
}

export async function recoverAccountPhrase(accountXid: string): Promise<string> {
  const result = await unlockPasskeySeed(undefined, true);
  try {
    const local = await loadIdentity(accountXid);
    const identity = await deriveIdentityKey(result.seed);
    if (result.accountXid !== accountXid || !local || toBase64url(local.publicKeyRaw) !== toBase64url(identity.publicKeyRaw)) throw new Error("Choose the saved sign-in for the account you are using now.");
    if (!result.recoveryPhrase) throw new Error("This saved sign-in has no recovery phrase backup. Use your original saved copy.");
    return result.recoveryPhrase;
  } finally { result.seed.fill(0); }
}

export function passkeyError(error: unknown): string {
  if (error instanceof Error && error.name === "NotAllowedError") return "Nothing changed. The sign-in prompt was closed or timed out. You can try again.";
  if (error instanceof Error && error.name === "InvalidStateError") return "That provider already has a passkey for this account. Try signing in with it, or choose another provider.";
  if (error instanceof Error && error.name === "OperationError") return "The encrypted backup could not be unlocked. Use your recovery phrase; no keys were replaced.";
  return error instanceof Error ? error.message : "Passkey setup failed. Your recovery phrase still works.";
}
